import * as React from 'react';
import { PRODUCT_GRID } from 'elc-service-view-names';
import styled, { css } from 'styled-components';
import { ServiceView, translate, ITranslationsCollection } from 'elc-service';
import { classes } from '../../utils/Classes';
import { CONTENT_SEARCH_ITEMS_LIMIT, TOGGLE_TYPE } from '../../constants/Search';
import { SearchArticle } from '../content-search/SearchArticle';
import { SearchTrendingTerm } from '../content-search/SearchTrendingTerm';
import { SEARCH_VIEW } from '../../../internal/constants/Search';
import {
    IArticleObjItem,
    IItem,
    IToutItem,
    ITrendingTermItem
} from '../../interfaces/ISearchInterfaces';
import { SearchConfigs } from '../../configs/SearchConfigs';
import { ContentSpacing, breakpoint } from 'elc-base-theme';
import { enhancedGridWrapper } from '../../../theme/default-theme';
import { observer } from 'mobx-react';

export interface ISearchContentGrid {
    config: {
        content?: IToutItem[];
        items?: IItem[];
        articles?: IArticleObjItem[];
        trendingTerms?: ITrendingTermItem[];
        hideProductPrice?: boolean;
        hideProductBadge?: boolean;
        showSubDisplayName?: boolean;
        hideProductRating?: boolean;
        enableNotifyMe?: boolean;
        enableQuickView?: boolean;
        enableMobileQuickView?: boolean;
        ctaType?: string;
        enableAutoReplenishment?: boolean;
        autoReplenishOptions?: number[];
        searchDisplayFormat?: string;
        autoReplenishToggleType?: TOGGLE_TYPE;
        searchTerm?: string;
        searchUrl?: string;
    };
    view: number;
    translations?: ITranslationsCollection;
}

interface IEnhancedSearchProps {
    onlyTrendingTermsEnabled?: boolean;
    onlyFavoritesEnabled?: boolean;
}

const SearchContentGridWrapper = styled.div`
    display: grid;
    grid-gap: 0;
    grid-auto-flow: dense;
    grid-template-columns: repeat(auto-fill, minmax(100%, 1fr));
    padding: 0 ${ContentSpacing.space20};
    ${breakpoint('desktop')`
        grid-gap: 41px;
        grid-template-columns: repeat(auto-fill, minmax(315px, 1fr));
        padding: 0;
    `}
`;

const SearchEnhancedWrapper = styled.div<IEnhancedSearchProps>`
    width: 100%;
    height: 100% ${enhancedGridWrapper};
`;

const SearchEnhancedTrendingTermsWrapper = styled.div<IEnhancedSearchProps>`
    width: 24%;
    float: left;
    padding-left: 64px;
    ${enhancedGridWrapper};
    ${({ onlyFavoritesEnabled }) =>
        onlyFavoritesEnabled &&
        css`
            display: none;
        `}
`;

const SearchEnhancedGridWrapper = styled.div<IEnhancedSearchProps>`
    width: 76%;
    float: right;
    padding-left: 30px;
    ${enhancedGridWrapper};
    ${({ onlyFavoritesEnabled }) =>
        onlyFavoritesEnabled &&
        css`
            width: 100%;
        `}
    ${({ onlyTrendingTermsEnabled }) =>
        onlyTrendingTermsEnabled &&
        css`
            display: none;
        `}
`;

@translate(['trendingTermsTitle', 'trendingResultsTitle'])
@observer
export class SearchContentGrid extends React.Component<ISearchContentGrid> {
    private searchContentGridClassnames = classes('search-content-grid-wrapper');
    private searchEnhancedGridClassnames = classes('search-enhanced-grid-wrapper');
    private trendingTermsTitleClassnames = classes('search-enhanced-trending-title');
    private trendingResultsTitleClassnames = classes('search-enhanced-grid-title');
    private searchEnhancedWrapperClassnames = classes('search-enhanced-wrapper');
    private searchEnhancedTrendingTermsWrapperClassnames = classes(
        'search-enhanced-trending-terms-wrapper'
    );
    public render() {
        const {
            config: { articles = [], content = [], trendingTerms = [], searchUrl, searchTerm },
            view
        } = this.props;
        let { items = [] } = this.props.config;

        const { trendingTermsTitle, trendingResultsTitle } = this.props
            .translations as ITranslationsCollection;

        let onlyFavoritesEnabled = false;
        let onlyTrendingTermsEnabled = false;

        if (!trendingTerms.length) {
            onlyFavoritesEnabled = true;
        }

        if (!items.length) {
            onlyTrendingTermsEnabled = true;
        }

        if (items.length > CONTENT_SEARCH_ITEMS_LIMIT && content.length) {
            items = items.slice(0, CONTENT_SEARCH_ITEMS_LIMIT - 1);
        } else if (items.length > CONTENT_SEARCH_ITEMS_LIMIT && content.length === 0) {
            items = items.slice(0, CONTENT_SEARCH_ITEMS_LIMIT);
        }

        const searchViewConfig = {
            ...this.props.config,
            items,
            content,
            productGridBreakpoints: {
                numColumnsMobile: 1,
                numColumnsDesktop: CONTENT_SEARCH_ITEMS_LIMIT
            },
            hideSorting: SearchConfigs.hideSorting,
            filterData: SearchConfigs.filterData
        };

        return view === SEARCH_VIEW.ENHANCED_SEARCH_OVERLAY ||
            view === SEARCH_VIEW.ENHANCED_SEARCH_RESULTS_PAGE ? (
            <SearchEnhancedWrapper className={this.searchEnhancedWrapperClassnames}>
                <SearchEnhancedTrendingTermsWrapper
                    className={this.searchEnhancedTrendingTermsWrapperClassnames}
                    onlyFavoritesEnabled={onlyFavoritesEnabled}
                >
                    <p className={this.trendingTermsTitleClassnames}>{trendingTermsTitle}</p>
                    {trendingTerms.map((trendingTerm, index) => (
                        <SearchTrendingTerm
                            key={`trendingTerm-${index}`}
                            trendingTerm={trendingTerm}
                            searchUrl={searchUrl}
                            view={view}
                        />
                    ))}
                </SearchEnhancedTrendingTermsWrapper>
                <SearchEnhancedGridWrapper
                    className={this.searchEnhancedGridClassnames}
                    onlyFavoritesEnabled={onlyFavoritesEnabled}
                    onlyTrendingTermsEnabled={onlyTrendingTermsEnabled}
                >
                    <p className={this.trendingResultsTitleClassnames}>{trendingResultsTitle}</p>
                    <ServiceView name={PRODUCT_GRID} config={searchViewConfig} />
                </SearchEnhancedGridWrapper>
            </SearchEnhancedWrapper>
        ) : (
            <SearchContentGridWrapper className={this.searchContentGridClassnames}>
                {articles.map(article => (
                    <SearchArticle
                        key={`article-${article.nId}`}
                        article={article}
                        view={view}
                        searchTerm={searchTerm}
                    />
                ))}
            </SearchContentGridWrapper>
        );
    }
}
